import { useCallback } from "react";

import type { ExportFormat } from "@asd/customer-portal-ui";
import { decomposeColor, useTheme } from "@mui/material/styles";

export type XlsxCell = {
  value: string | number | Date | null;
  numFmt?: string;
  // Any CSS color (hex, rgb(), etc.)
  color?: string;
};

const XLSX_MIME = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

export const downloadBlob = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const downloadCsv = (csvText: string, filename: string) => {
  downloadBlob(new Blob([csvText], { type: "text/csv;charset=utf-8;" }), `${filename}.csv`);
};

// RFC 4180 parser: handles quoted fields, escaped quotes ("") and newlines inside quotes
export const parseCsv = (text: string): string[][] => {
  // Strip a leading byte order mark, if the server sent one
  const input = text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;
  const rows: string[][] = [];
  let row: string[] = [];
  let field = "";
  let inQuotes = false;

  for (let i = 0; i < input.length; i++) {
    const char = input[i];

    if (inQuotes) {
      if (char === '"' && input[i + 1] === '"') {
        field += '"';
        i++;
      } else if (char === '"') {
        inQuotes = false;
      } else {
        field += char;
      }
    } else if (char === '"') {
      inQuotes = true;
    } else if (char === ",") {
      row.push(field);
      field = "";
    } else if (char === "\n" || char === "\r") {
      if (char === "\r" && input[i + 1] === "\n") i++;
      row.push(field);
      rows.push(row);
      row = [];
      field = "";
    } else {
      field += char;
    }
  }

  if (field || row.length) {
    row.push(field);
    rows.push(row);
  }

  return rows;
};

const decimalFormat = (decimals: string | undefined, grouped: boolean) =>
  `${grouped ? "#,##0" : "0"}${decimals ? `.${"0".repeat(decimals.length)}` : ""}`;

// Converts a display string into a typed Excel value, so numbers, currency, percentages and dates
// can be sorted/summed in Excel while still looking the same as they do in the table
export const inferXlsxCell = (raw: string): XlsxCell => {
  const value = raw.trim();
  if (!value) return { value: null };

  // $1,234.56, -$1,234.56, ($1,234.56)
  const currency = value.match(/^(\(?)(-?)\$(-?)(\d{1,3}(?:,\d{3})*|\d+)(\.\d+)?(\)?)$/);
  if (currency) {
    const [, openParen, minusBefore, minusAfter, whole, fraction = "", closeParen] = currency;
    const negative = !!(minusBefore || minusAfter || (openParen && closeParen));
    const amount = Number(`${whole.replace(/,/g, "")}${fraction}`);
    return { value: negative ? -amount : amount, numFmt: '"$"#,##0.00;-"$"#,##0.00' };
  }

  const percent = value.match(/^(-?\d+)(?:\.(\d+))?%$/);
  if (percent) {
    return { value: Number(value.slice(0, -1)) / 100, numFmt: `${decimalFormat(percent[2], false)}%` };
  }

  // 1,234 or 1,234.5
  const grouped = value.match(/^-?\d{1,3}(?:,\d{3})+(?:\.(\d+))?$/);
  if (grouped) {
    return { value: Number(value.replace(/,/g, "")), numFmt: decimalFormat(grouped[1], true) };
  }

  const decimal = value.match(/^-?\d+\.(\d+)$/);
  if (decimal) {
    return { value: Number(value), numFmt: decimalFormat(decimal[1], false) };
  }

  // Plain integers, but leave anything that looks like an identifier (leading zeros, long digit strings) as text
  if (/^-?(0|[1-9]\d{0,8})$/.test(value)) {
    return { value: Number(value) };
  }

  // MM/DD/YYYY or YYYY-MM-DD, built in UTC so Excel shows the same calendar date regardless of timezone
  const usDate = value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  const isoDate = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const dateParts = usDate ? [usDate[3], usDate[1], usDate[2]] : isoDate ? [isoDate[1], isoDate[2], isoDate[3]] : null;
  if (dateParts) {
    const [year, month, day] = dateParts.map(Number);
    const date = new Date(Date.UTC(year, month - 1, day));
    // Reject impossible dates like 02/31/2026, which Date would silently roll over
    if (date.getUTCMonth() === month - 1 && date.getUTCDate() === day) {
      return { value: date, numFmt: "mm/dd/yyyy" };
    }
  }

  return { value };
};

const toArgb = (cssColor: string) => {
  const { type, values } = decomposeColor(cssColor);
  if (!type.startsWith("rgb")) return undefined;
  return `FF${values
    .slice(0, 3)
    .map((v) => Math.round(v).toString(16).padStart(2, "0"))
    .join("")
    .toUpperCase()}`;
};

// Excel sheet names are max 31 chars and cannot contain : \ / ? * [ ]
const toSheetName = (name: string) => name.replace(/[:\\/?*[\]]/g, " ").slice(0, 31) || "Sheet1";

export const downloadXlsx = async ({
  filename,
  headerColor,
  headers,
  rows,
  sheetName = filename,
}: {
  filename: string;
  headerColor: string;
  headers: string[];
  rows: XlsxCell[][];
  sheetName?: string;
}) => {
  // Loaded on demand so exceljs isn't part of the main bundle
  const { Workbook } = await import("exceljs");

  const workbook = new Workbook();
  const worksheet = workbook.addWorksheet(toSheetName(sheetName), {
    views: [{ state: "frozen", ySplit: 1 }],
  });

  const headerRow = worksheet.addRow(headers);
  headerRow.font = { bold: true, color: { argb: "FFFFFFFF" } };
  headerRow.alignment = { vertical: "middle" };
  const headerArgb = toArgb(headerColor);
  headerRow.eachCell((cell) => {
    if (headerArgb) cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: headerArgb } };
  });

  rows.forEach((cells) => {
    const row = worksheet.addRow(cells.map((cell) => cell.value));
    cells.forEach((cell, index) => {
      const excelCell = row.getCell(index + 1);
      if (cell.numFmt) excelCell.numFmt = cell.numFmt;
      const argb = cell.color ? toArgb(cell.color) : undefined;
      if (argb) excelCell.font = { color: { argb } };
    });
  });

  if (headers.length) {
    worksheet.autoFilter = { from: { row: 1, column: 1 }, to: { row: 1, column: headers.length } };
  }

  // Size each column to its longest value, within reason
  worksheet.columns.forEach((column, index) => {
    const longest = Math.max(
      headers[index]?.length ?? 0,
      ...rows.map((cells) => {
        const value = cells[index]?.value;
        if (value instanceof Date) return 10;
        if (typeof value === "number") return value.toLocaleString("en-US", { maximumFractionDigits: 2 }).length + 2;
        return Math.max(
          ...String(value ?? "")
            .split("\n")
            .map((line) => line.length),
        );
      }),
    );
    column.width = Math.min(Math.max(longest + 2, 8), 60);
  });

  const buffer = await workbook.xlsx.writeBuffer();
  downloadBlob(new Blob([buffer], { type: XLSX_MIME }), `${filename}.xlsx`);
};

export const csvToXlsxDownload = (csvText: string, filename: string, headerColor: string) => {
  const [headers = [], ...rows] = parseCsv(csvText).filter((row) => row.some((cell) => cell !== ""));
  return downloadXlsx({ filename, headerColor, headers, rows: rows.map((row) => row.map(inferXlsxCell)) });
};

const toText = async (data: unknown) => (typeof data === "string" ? data : new Blob([data as BlobPart]).text());

// Downloads CSV export data either as-is or converted to a formatted Excel workbook
export const useTableExport = () => {
  const theme = useTheme();

  return useCallback(
    async (data: unknown, filename: string, format: ExportFormat) => {
      if (data == null) return;
      const csvText = await toText(data);

      if (format === "xlsx") {
        await csvToXlsxDownload(csvText, filename, theme.palette.primary.main);
      } else {
        downloadCsv(csvText, filename);
      }
    },
    [theme],
  );
};
